import { ApplicationConfig, LOCALE_ID } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import localeEsMX from '@angular/common/locales/es-MX';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './auth.interceptor';

import { MSAL_INSTANCE, MsalService } from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';

registerLocaleData(localeEsMX, 'es-MX');

/**
 * Instancia de MSAL para el inicio de sesión con cuenta Microsoft (Entra ID).
 *
 * Se registra únicamente `MsalService`: NO se usa `MsalInterceptor`. Dos razones:
 *  1. `provideHttpClient` de esta app usa interceptores funcionales (`withInterceptors`),
 *     mientras que `MsalInterceptor` es class-based y exigiría `withInterceptorsFromDi()`.
 *  2. Las llamadas a `/api/**` deben viajar con el JWT propio de SCO que inyecta
 *     `authInterceptor`, no con el token de Entra. El token de Entra se usa una sola vez,
 *     contra `POST /api/auth/sso`, y se descarta.
 *
 * La inicialización de MSAL es diferida: se hace en el flujo de login (`login.component.ts`),
 * para no pagar ese costo en cada arranque cuando el SSO está deshabilitado.
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.entra.clientId,
      authority: `https://login.microsoftonline.com/${environment.entra.tenantId}`,
      redirectUri: environment.entra.redirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      // sessionStorage: el token de Entra no sobrevive al cierre de la pestaña.
      // No necesita persistir, solo se canjea por la sesión de SCO y se descarta.
      cacheLocation: BrowserCacheLocation.SessionStorage,
      storeAuthStateInCookie: false,
    },
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimations(),
    { provide: LOCALE_ID, useValue: 'es-MX' },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    MsalService,
  ],
};
